import { Injectable, inject, ApplicationRef } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { MatDialog } from '@angular/material/dialog';
import { filter, switchMap, tap, timer } from 'rxjs';
import { PwaDialogData, PwaUpdateDialogComponent } from './pwa-update-dialog.component';
import { environment } from '@env';

@Injectable({ providedIn: 'root' })
export class PwaService {
    readonly #swUpdate = inject(SwUpdate);
    readonly #dialog = inject(MatDialog);
    readonly #appRef = inject(ApplicationRef);

    readonly #checkIntervalMs = 60 * 1000; // Check every 1 minute
    #isDialogOpen = false;

    init(): void {
        if (!this.#swUpdate.isEnabled) {
            console.log('[PWA] Service worker not enabled');
        } else {
            this.#listenForUpdates();
            this.#listenForUnrecoverableState();
            this.#scheduleUpdateChecks();
        }

        // Expose test methods in dev mode for browser console testing
        if (environment.envName !== 'prod') {
            (window as unknown as { testPwaUpdate: () => void; testPwaUnrecoverable: () => void }).testPwaUpdate = () =>
                this.showUpdateDialog();
            (window as unknown as { testPwaUpdate: () => void; testPwaUnrecoverable: () => void }).testPwaUnrecoverable = () =>
                this.showUnrecoverableDialog();
            console.log('[PWA] Dev mode: call testPwaUpdate() or testPwaUnrecoverable() in console to test dialogs');
        }
    }

    /**
     * Public method to manually show the update dialog (for testing)
     */
    showUpdateDialog(): void {
        this.#showUpdateDialog(true);
    }

    /**
     * Public method to manually show the unrecoverable dialog (for testing)
     */
    showUnrecoverableDialog(): void {
        this.#showUnrecoverableDialog('Test: Simulated unrecoverable state', true);
    }

    #listenForUpdates(): void {
        this.#swUpdate.versionUpdates.subscribe((event) => {
            console.log('[PWA] Version update event:', event.type, event);

            switch (event.type) {
                case 'VERSION_DETECTED':
                    console.log('[PWA] Downloading new app version:', event.version.hash);
                    break;
                case 'VERSION_READY':
                    console.log('[PWA] New version ready:', event.latestVersion.hash);
                    this.#showUpdateDialog();
                    break;
                case 'VERSION_INSTALLATION_FAILED':
                    console.error('[PWA] Version installation failed with:', event.version.hash, event.error);
                    // Hash mismatch - clear service worker and caches, then show update dialog
                    this.#handleInstallationFailed();
                    break;
                case 'NO_NEW_VERSION_DETECTED':
                    console.log('[PWA] No new version detected');
                    break;
            }
        });
    }

    #listenForUnrecoverableState(): void {
        this.#swUpdate.unrecoverable.subscribe((event) => {
            console.error('[PWA] Unrecoverable state:', event.reason);
            this.#showUnrecoverableDialog(event.reason);
        });
    }

    #scheduleUpdateChecks(): void {
        // Wait for app to stabilize before starting periodic checks
        this.#appRef.isStable
            .pipe(
                tap((stable) => console.log('[PWA] App is stable, starting update checks...', stable)),
                filter((stable) => !!stable),
                switchMap(() => timer(500, this.#checkIntervalMs)),
            )
            .subscribe(() => {
                console.log('[PWA] Checking for updates...');
                this.#swUpdate.checkForUpdate().catch((err) => {
                    console.error('[PWA] Error checking for updates:', err);
                });
            });
    }

    #showUpdateDialog(isTest = false): void {
        if (this.#isDialogOpen) {
            return;
        }

        this.#isDialogOpen = true;
        const dialogRef = this.#dialog.open<PwaUpdateDialogComponent, PwaDialogData, boolean>(PwaUpdateDialogComponent, {
            hasBackdrop: false,
            closeOnNavigation: false,
            position: {
                bottom: '16px',
                left: '16px',
            },
            panelClass: 'pwa-update-dialog',
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((shouldUpdate) => {
            this.#isDialogOpen = false;
            if (shouldUpdate && !isTest) {
                this.#applyUpdate();
            } else if (shouldUpdate && isTest) {
                console.log('[PWA] Test mode: would reload the page now');
            }
        });
    }

    #applyUpdate(): void {
        this.#swUpdate
            .activateUpdate()
            .then(() => {
                window.location.reload();
            })
            .catch(() => {
                window.location.reload();
            });
    }

    #showUnrecoverableDialog(reason: string, isTest = false): void {
        if (this.#isDialogOpen) {
            return;
        }

        this.#isDialogOpen = true;
        const dialogRef = this.#dialog.open<PwaUpdateDialogComponent, PwaDialogData, boolean>(PwaUpdateDialogComponent, {
            hasBackdrop: true,
            closeOnNavigation: false,
            position: {
                bottom: '16px',
                left: '16px',
            },
            panelClass: 'pwa-update-dialog',
            disableClose: true,
            data: { isUnrecoverable: true, reason },
        });

        dialogRef.afterClosed().subscribe(() => {
            this.#isDialogOpen = false;
            if (isTest) {
                console.log('[PWA] Test mode: would reload the page now');
            } else {
                window.location.reload();
            }
        });
    }

    async #handleInstallationFailed(): Promise<void> {
        console.log('[PWA] Clearing service worker and caches...');

        try {
            // Unregister service worker
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
                console.log('[PWA] Service worker unregistered');
            }

            // Clear all caches
            const cacheNames = await caches.keys();
            for (const cacheName of cacheNames) {
                await caches.delete(cacheName);
                console.log('[PWA] Cache deleted:', cacheName);
            }

            console.log('[PWA] Caches cleared, showing update dialog...');
            // Show dialog to let user choose when to reload
            this.#showUpdateDialog();
        } catch (error) {
            console.error('[PWA] Error clearing service worker:', error);
            // Show dialog anyway
            this.#showUpdateDialog();
        }
    }
}
